import { useState } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { LayoutDashboard, Home as HomeIcon, Plug, Sparkles, CalendarCheck, Star, Mountain, LogOut, Compass, ExternalLink, FileText, Building2, Receipt, CalendarRange, GraduationCap, Ticket, Menu, X, Newspaper } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import AdminDashboard from "./Dashboard";
import AdminSettings from "./Settings";
import AdminApartments from "./Apartments";
import AdminPricing from "./Pricing";
import AdminBookings from "./Bookings";
import AdminReviews from "./Reviews";
import AdminExperiences from "./Experiences";
import AdminSiteContent from "./SiteContent";
import AdminQuote from "./Quote";
import AdminCalendar from "./Calendar";
import AdminKnowledge from "./Knowledge";
import AdminDiscountCodes from "./DiscountCodes";
import AdminBlog from "./Blog";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/calendar", icon: CalendarRange, label: "Calendario" },
  { to: "/admin/homepage", icon: FileText, label: "Contenuti Homepage" },
  { to: "/admin/apartments", icon: Building2, label: "Strutture" },
  { to: "/admin/experiences", icon: Compass, label: "Esperienze & Convenzionati" },
  { to: "/admin/knowledge", icon: GraduationCap, label: "Assistente IA" },
  { to: "/admin/blog", icon: Newspaper, label: "Blog & Guide" },
  { to: "/admin/pricing", icon: Sparkles, label: "Dynamic Pricing IA" },
  { to: "/admin/quote", icon: Receipt, label: "Preventivo" },
  { to: "/admin/discount-codes", icon: Ticket, label: "Codici Sconto" },
  { to: "/admin/bookings", icon: CalendarCheck, label: "Prenotazioni" },
  { to: "/admin/reviews", icon: Star, label: "Recensioni" },
  { to: "/admin/settings", icon: Plug, label: "Integrazioni" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-ivory text-fir">Caricamento…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-stone/40">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-fir-deep text-ivory flex items-center justify-between px-4 z-30">
        <div className="flex items-center gap-2">
          <Mountain className="text-gold" size={22} />
          <span className="font-display text-xl">Appartamenti in Val di Sole</span>
        </div>
        <button data-testid="admin-menu-toggle" onClick={() => setOpen(true)} className="p-2" aria-label="Apri menu"><Menu size={24} /></button>
      </div>

      {/* Mobile overlay */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} data-testid="admin-nav-overlay" />}

      <aside className={`w-64 bg-fir-deep text-ivory flex flex-col fixed h-full z-50 transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 flex items-center justify-between border-b border-ivory/10">
          <div className="flex items-center gap-2">
            <Mountain className="text-gold" size={26} />
            <span className="font-display text-2xl">Appartamenti in Val di Sole</span>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 text-ivory/70 hover:text-gold" aria-label="Chiudi menu"><X size={22} /></button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-gold text-fir-deep font-semibold" : "text-ivory/75 hover:bg-ivory/10"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-ivory/10 space-y-2">
          <NavLink to="/" data-testid="view-site-link" target="_blank"
            className="flex items-center gap-2 text-ivory/70 hover:text-gold text-sm">
            <ExternalLink size={16} /> Vai al sito
          </NavLink>
          <p className="text-xs text-ivory/50 truncate">{user.email}</p>
          <button data-testid="logout-btn" onClick={async () => { await logout(); window.location.assign("/"); }}
            className="flex items-center gap-2 text-ivory/80 hover:text-gold text-sm">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8 min-w-0 overflow-x-hidden">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="homepage" element={<AdminSiteContent />} />
          <Route path="apartments" element={<AdminApartments />} />
          <Route path="experiences" element={<AdminExperiences />} />
          <Route path="knowledge" element={<AdminKnowledge />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="quote" element={<AdminQuote />} />
          <Route path="discount-codes" element={<AdminDiscountCodes />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}
